import type { LayerId } from "@/src/components/Map/LayerControl";
import { stableCustomLayerId } from "./wmsCatalog";

/**
 * Jakie wbudowane warstwy (top-level) są DOSTĘPNE dla danej procedury.
 *
 * Kontekst:
 *  - Procedura typu "plan ogólny" (spatialPlanningActType zawiera "3.1")
 *    → dostępna warstwa `plan` (wektorowy POG), ale nie `raster` (jest dla MPZP).
 *  - Procedura typu MPZP z wgranym rasterem planu → dostępna warstwa `raster`,
 *    ale nie `plan`.
 *  - Warstwy bazowe (`dzialki, budynki, studium, mpzp, ortho, osm`) dostępne
 *    zawsze.
 *
 * Admin w EditProcedurePage widzi tę listę minus `hiddenBuiltinLayerIds`.
 * Public (TenantPublicPage) używa tego samego — dzięki temu admin nie może
 * "przypadkowo" pokazać POG w procedurze MPZP (bo tam nie ma danych planu),
 * a user widzi dokładnie to co admin skonfigurował.
 */
export function getAvailableBuiltinLayerIds(options: {
  spatialPlanningActType?: string;
  hasRasterReady?: boolean;
  hiddenBuiltinLayerIds?: string[];
}): LayerId[] {
  const base: LayerId[] = ["dzialki", "budynki", "studium", "mpzp", "ortho", "cieniowanie", "osm"];
  const actType = options.spatialPlanningActType ?? "";
  const isPlanOgolny = actType.includes("3.1") || /plan og[oó]lny/i.test(actType);

  let all: LayerId[];
  if (isPlanOgolny) all = ["plan", ...base];
  else if (options.hasRasterReady) all = ["raster", ...base];
  else all = base;

  const hidden = new Set(options.hiddenBuiltinLayerIds ?? []);
  return all.filter((id) => !hidden.has(id));
}

/**
 * Migracja `defaultLayerOrder` dla starych zapisów payloadu.
 *
 * Kontekst: do commita 28e16b4 custom warstwy miały efemeryczne ID
 * (`wms_admin_0_*`, `catalog_0_*`, `wms_${Date.now()}_*`), które zmieniały
 * się przy każdej hydratacji. `defaultLayerOrder` zapisane wtedy zawiera
 * te stare ID, więc po deploy-u nowego kodu (stabilne ID) nie matchują
 * one z aktualnymi ID warstw — custom trafiają zawsze na koniec listy.
 *
 * Fix: przy hydratacji, jeśli w order są jakieś `custom:...` ID które
 * NIE istnieją wśród aktualnych stabilnych ID, ale liczba custom w order
 * zgadza się z liczbą custom w payload — zamieniamy je pozycyjnie
 * (i-ty stary ID → i-ty nowy stabilny ID).
 */
export function migrateCustomOrder(
  orderFromPayload: string[] | undefined,
  payloadCustomLayers: Array<{ url: string; layerName: string; catalogId?: string }> | undefined,
): string[] | undefined {
  if (!Array.isArray(orderFromPayload) || orderFromPayload.length === 0) return orderFromPayload;
  if (!Array.isArray(payloadCustomLayers) || payloadCustomLayers.length === 0) return orderFromPayload;

  const newStableIds = payloadCustomLayers.map(
    (l) => `custom:${stableCustomLayerId({ catalogId: l.catalogId, url: l.url, layerName: l.layerName })}`,
  );
  const newStableSet = new Set(newStableIds);

  // Jeśli wszystkie `custom:...` z order są już w stabilnych ID — nic nie trzeba migrować.
  const orderCustomIds = orderFromPayload.filter((id) => id.startsWith("custom:"));
  const allMatch = orderCustomIds.every((id) => newStableSet.has(id));
  if (allMatch) return orderFromPayload;

  // Jeśli liczba custom w order NIE zgadza się z liczbą custom w payload,
  // nie możemy jednoznacznie zmapować — zwracamy order bez zmian (fallback
  // do "doklejania na końcu" w resolveRenderOrder).
  if (orderCustomIds.length !== newStableIds.length) return orderFromPayload;

  // Pozycyjna migracja: i-ty custom w order → i-ty nowy stabilny ID.
  let customIdx = 0;
  return orderFromPayload.map((id) => {
    if (id.startsWith("custom:")) {
      return newStableIds[customIdx++] ?? id;
    }
    return id;
  });
}
